import {
  GroupAttendanceAggregateCellResponse,
  GroupAttendanceAggregateSessionEntryResponse,
  GroupAttendanceAggregateTrackingPointResponse,
} from '@schooly/api';

import { AttendanceGridSession, AttendanceGridTrackingPoint } from './types';

export type PeriodToTake = { id: string; name: string };

type CellMeta = {
  period_id?: string | null;
  sessions: AttendanceGridSession[];
};

type TrackingPointSource = {
  session: AttendanceGridSession | null;
  period: { lesson: { id: string; name: string | null; order: number | null } } | null;
  total_count?: number;
  tracked_count?: number;
};

const SESSION_LABEL: Record<AttendanceGridSession, string> = { am: 'AM', pm: 'PM' };

export const toSession = (session: string | null): AttendanceGridSession | null =>
  session === 'am' || session === 'pm' ? session : null;

const buildLabel = (session: AttendanceGridSession | null, label: string | null): string => {
  if (session && label) return `${SESSION_LABEL[session]}/${label}`;
  if (session) return SESSION_LABEL[session];
  return label ?? '';
};

/** Short order label within the structure snapshot, e.g. "5" — used in the compact header. */
const periodLabel = (point: TrackingPointSource): string =>
  point.period?.lesson.order != null ? String(point.period.lesson.order) : '';

export const buildTrackingPoints = (
  trackingPoints: TrackingPointSource[],
): AttendanceGridTrackingPoint[] =>
  trackingPoints.map((point) => {
    const session = toSession(point.session);
    const period = point.period
      ? {
          id: point.period.lesson.id,
          label: periodLabel(point),
          name: point.period.lesson.name ?? '',
        }
      : null;

    return {
      key: period ? period.id : (session ?? ''),
      kind: period ? 'period' : 'session',
      session,
      period,
      label: buildLabel(session, period?.label ?? null),
      complete: !!point.total_count && point.tracked_count === point.total_count,
    };
  });

export const selectPeriodsToTake = (
  trackingPoints: GroupAttendanceAggregateTrackingPointResponse[],
): PeriodToTake[] =>
  trackingPoints
    .filter(
      (point) =>
        !!point.period &&
        !point.session &&
        point.total_count > 0 &&
        point.tracked_count < point.total_count,
    )
    .map((point) => ({ id: point.period!.lesson.id, name: point.period!.lesson.name ?? '' }));

/**
 * A register created before the per-period contract has no flat entry list on
 * its cells, so read it as empty instead of letting `undefined` reach callers.
 */
export const cellEntries = (
  value: Pick<GroupAttendanceAggregateCellResponse['value'], 'attendance_entries'>,
): GroupAttendanceAggregateSessionEntryResponse[] => value.attendance_entries ?? [];

export const pickEntryForPoint = <
  T extends Pick<GroupAttendanceAggregateSessionEntryResponse, 'session'>,
>(
  entries: T[],
  point: AttendanceGridTrackingPoint,
): T | null => {
  if (point.session) {
    const sessionEntry = entries.find((entry) => toSession(entry.session) === point.session);
    if (sessionEntry) return sessionEntry;
    if (!point.period) return null;
  }

  return entries.find((entry) => entry.session === null) ?? null;
};

export const trackingPointKey = (
  session: AttendanceGridSession | null,
  periodId: string | null,
): string => `${session ?? ''}:${periodId ?? ''}`;

export const cellBelongsToPoint = (meta: CellMeta, point: AttendanceGridTrackingPoint): boolean => {
  if ((meta.period_id ?? null) !== (point.period?.id ?? null)) return false;
  if (!point.session) return meta.sessions.length === 0;

  return meta.sessions.some((session) => toSession(session) === point.session);
};

export const findCellForPoint = <T extends { meta: CellMeta }>(
  cells: T[] | undefined,
  point: AttendanceGridTrackingPoint,
): T | undefined => cells?.find((cell) => cellBelongsToPoint(cell.meta, point));
